import { apiFetch } from '@/lib/api';

export interface AIModelConfig {
  model: string;
  provider: 'gemini' | 'openai';
}

export interface ModelInfo {
  name: string;
  provider: string;
  description: string;
  costPer1M: string;
}

export interface AISettings {
  current: {
    enrichment: AIModelConfig;
    research: AIModelConfig;
  };
  available: {
    enrichment: Record<string, ModelInfo>;
    research: Record<string, ModelInfo>;
  };
}

export class SettingsService {
  /**
   * Get AI settings (current models and available options)
   */
  async getAISettings(): Promise<AISettings> {
    const response = await apiFetch('/api/settings/ai');
    if (!response.ok) {
      throw new Error('Failed to fetch AI settings');
    }
    const result = await response.json();
    return result.data; // Unwrap standard envelope
  }

  /**
   * Update AI enrichment model
   */
  async updateEnrichmentModel(model: string): Promise<void> {
    const response = await apiFetch('/api/settings/ai/enrichment', {
      method: 'PUT',
      body: JSON.stringify({ model }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update enrichment model' }));
      throw new Error(error.message || 'Failed to update enrichment model');
    }
  }

  /**
   * Update AI research model
   */
  async updateResearchModel(model: string): Promise<void> {
    const response = await apiFetch('/api/settings/ai/research', {
      method: 'PUT',
      body: JSON.stringify({ model }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update research model' }));
      throw new Error(error.message || 'Failed to update research model');
    }
  }

  /**
   * Get Cvent settings
   */
  async getCventSettings(): Promise<{ browserCookies: string }> {
    const response = await apiFetch('/api/settings/cvent');
    if (!response.ok) {
      throw new Error('Failed to fetch Cvent settings');
    }
    const result = await response.json();
    return result.data; // Unwrap standard envelope
  }

  /**
   * Update Cvent browser cookies
   */
  async updateCventBrowserCookies(cookies: string): Promise<void> {
    const response = await apiFetch('/api/settings/cvent/browser-cookies', {
      method: 'PUT',
      body: JSON.stringify({ cookies }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update Cvent browser cookies' }));
      throw new Error(error.message || 'Failed to update Cvent browser cookies');
    }
  }

  /**
   * Trigger Cvent login via Puppeteer to extract cookies
   */
  async triggerCventLogin(): Promise<{ cookies: string; message: string }> {
    const response = await apiFetch('/api/settings/cvent/login', {
      method: 'POST',
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to trigger Cvent login' }));
      throw new Error(error.message || 'Failed to trigger Cvent login');
    }
    
    const result = await response.json();
    return result.data;
  }

  /**
   * Get Cvent API settings (OAuth credentials)
   */
  async getCventApiSettings(): Promise<{ clientId: string; region: string; hasSecret: boolean }> {
    const response = await apiFetch('/api/settings/cvent-api');
    if (!response.ok) {
      throw new Error('Failed to fetch Cvent API settings');
    }
    const result = await response.json();
    return result.data;
  }

  /**
   * Update Cvent API OAuth credentials
   */
  async updateCventApiCredentials(
    clientId: string,
    clientSecret: string,
    region: 'na' | 'eu' | 'na-sbx'
  ): Promise<void> {
    const response = await apiFetch('/api/settings/cvent-api/credentials', {
      method: 'PUT',
      body: JSON.stringify({ clientId, clientSecret, region }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update Cvent API credentials' }));
      throw new Error(error.message || 'Failed to update Cvent API credentials');
    }
  }
}

